import type { Role } from '@shared/hooks/useAuthStore';

/**
 * T-211 — visibilità per ruolo nel backoffice.
 *
 * Rispecchia la GERARCHIA del RolesGuard del backend: elencare un ruolo
 * specialista (Editor / Shop Manager / Supporto) rende la voce visibile ANCHE ad
 * Admin e Developer, che li includono. SUPER_ADMIN vede tutto. Il backend resta
 * la barriera vera: qui nascondiamo solo menu e pagine e scegliamo l'atterraggio.
 */
const ROLE_GRANTS: Record<Role, Role[]> = {
  FAN: ['FAN'],
  CLUB_EDITOR: ['CLUB_EDITOR', 'FAN'],
  CLUB_SHOP_MANAGER: ['CLUB_SHOP_MANAGER', 'FAN'],
  CLUB_SUPPORT: ['CLUB_SUPPORT', 'FAN'],
  CLUB_ADMIN: ['CLUB_ADMIN', 'CLUB_EDITOR', 'CLUB_SHOP_MANAGER', 'CLUB_SUPPORT', 'FAN'],
  DEVELOPER: ['DEVELOPER', 'CLUB_ADMIN', 'CLUB_EDITOR', 'CLUB_SHOP_MANAGER', 'CLUB_SUPPORT', 'FAN'],
  SUPER_ADMIN: [
    'SUPER_ADMIN',
    'DEVELOPER',
    'CLUB_ADMIN',
    'CLUB_EDITOR',
    'CLUB_SHOP_MANAGER',
    'CLUB_SUPPORT',
    'FAN',
  ],
};

/** I ruoli che `userRole` soddisfa (se stesso + tutti quelli inclusi dalla gerarchia). */
export function grantsFor(userRole: Role | undefined): Role[] {
  if (!userRole) return [];
  return ROLE_GRANTS[userRole] ?? [userRole];
}

/**
 * Una voce (menu o rotta) è accessibile se non dichiara ruoli (aperta a chi è
 * autenticato) oppure se i ruoli richiesti intersecano i grant dell'utente.
 * SUPER_ADMIN passa sempre.
 */
export function hasNavAccess(userRole: Role | undefined, roles?: string[]): boolean {
  if (!userRole) return false;
  if (userRole === 'SUPER_ADMIN') return true;
  if (!roles || roles.length === 0) return true;
  const grants = grantsFor(userRole);
  return roles.some((r) => grants.includes(r as Role));
}

/**
 * Pagina di atterraggio dopo il login: la prima sezione a cui il ruolo ha
 * accesso, nell'ordine del menu (overview → sportiva → commerciale → loyalty).
 * L'ordine rispecchia le sezioni di NAV in Layout.tsx: aggiornalo se cambia.
 */
const LANDING_ORDER: { path: string; roles?: Role[] }[] = [
  { path: '/', roles: ['CLUB_ADMIN'] }, // overview / dashboard (solo Admin/Dev)
  { path: '/matches', roles: ['CLUB_EDITOR'] }, // Sportiva (Editor)
  { path: '/orders', roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] }, // Commerciale
  { path: '/loyalty', roles: ['CLUB_SUPPORT'] }, // Loyalty
];

export function landingPathForRole(userRole: Role | undefined): string {
  if (!userRole) return '/';
  if (userRole === 'SUPER_ADMIN') return '/';
  const hit = LANDING_ORDER.find((c) => hasNavAccess(userRole, c.roles));
  return hit ? hit.path : '/';
}
